import Button from '@/components/UI/Button';
import Header from '@/components/UI/Header';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { successToast } from '@/helpers';
import type { RegisteredDevice } from '@/model/Device.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { RouteService } from '@/support/RouteService';
import { DateUtils } from '@/utils/Date.utils';
import { modalAction } from '@/utils/modal';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const RegisterDevices: FC = () => {
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const settings = useSelector<{ ui: { settings: Record<string, any> } }>((state) => state.ui.settings);
  const deviceTableColumns: Array<TanStackColumnDef<RegisteredDevice>> = useMemo(
    () => [
      {
        id: 'title',
        header: 'Title',
        accessorKey: 'title',
        enableSorting: false,
        isVisible: true,
        cell: ({ row: { original: device }, table }: { row: { original: RegisteredDevice }; table?: any }) => (
          <span
            onClick={() =>
              openModal('EDIT_DEVICE', {
                onEditDevice: table?.options?.meta?.apiTable?.fetchTableData,
                device,
              })
            }
            className="link blue"
          >
            {device?.title ?? ''}
          </span>
        ),
      },
      {
        id: 'created_at',
        header: 'Created at',
        accessorKey: 'created_at',
        enableSorting: false,
        isVisible: true,
        cell: ({ row: { original } }: { row: { original: RegisteredDevice } }) => (
          <>{DateUtils.renderDateTime(original.created_at, { defaultResponse: '-' })}</>
        ),
      },
      {
        id: 'updated_at',
        header: 'Updated at',
        accessorKey: 'updated_at',
        enableSorting: false,
        isVisible: true,
        cell: ({ row: { original } }: { row: { original: RegisteredDevice } }) => (
          <>{DateUtils.renderDateTime(original.updated_at, { defaultResponse: '-' })}</>
        ),
      },
      {
        id: 'device_type_title',
        header: 'Device type',
        accessorKey: 'device_type_title',
        enableSorting: false,
        isVisible: true,
      },
      {
        id: 'usb_product_id',
        header: 'USB Product ID',
        accessorKey: 'usb_product_id',
        enableSorting: false,
        isVisible: true,
      },
      {
        id: 'product_name',
        header: 'Product name',
        accessorKey: 'product_name',
        enableSorting: false,
        isVisible: true,
      },
      {
        id: 'usb_vendor_id',
        header: 'USB Vendor ID',
        accessorKey: 'usb_vendor_id',
        enableSorting: false,
        isVisible: true,
      },
      {
        id: 'vendor_name',
        header: 'Vendor name',
        accessorKey: 'vendor_name',
        enableSorting: false,
        isVisible: true,
      },
    ],
    []
  );

  const { invoke: deleteDevices } = useApiHook({
    endpoint: 'DELETE /api/v1/devices',
    invokeOnInit: false,
  });

  const handleDeleteDevices = async (
    deviceApiIds: Array<RegisteredDevice['id']>,
    refetchTableData: () => Promise<void>
  ) => {
    try {
      await deleteDevices({ body: { device_api_ids: deviceApiIds } });
      await refetchTableData();
      successToast(`Deleted ${deviceApiIds.length} device${deviceApiIds.length > 1 ? 's' : ''}`);
    } catch (error) {
      // handled by `deleteDevices`
    }
  };

  const bulkActions: (props: UseTableProps<RegisteredDevice>) => Array<unknown> = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }) => {
    const deviceApiIds = selectedFlatRows.map(({ original: { id } }) => id);
    return [
      {
        name: `Delete device${selectedFlatRows?.length > 1 ? 's' : ''}`,
        className: 'red',
        action: () =>
          openModal('DELETE_DEVICES', {
            onClick: () => handleDeleteDevices(deviceApiIds, refetchTableData),
          }),
      },
    ];
  };

  return (
    <>
      <Header mainHeaderText="Devices" />
      <p className="f6 pb2">
        Register devices for use in your research.
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="dib ml1 link blue"
          href={RouteService.help('register_devices')}
        >
          Read more
        </a>
      </p>
      <div className="mv3">
        <APITable
          apiInfo={{
            type: 'internalApi',
            route: RouteService.api({
              endpoint: 'GET /api/v1/devices',
              path: undefined,
            }).url,
          }}
          bulkActions={bulkActions}
          columns={deviceTableColumns}
          defaultSortBy={{ id: 'created_at', desc: true }}
          searchPlaceholderText="Search devices"
          settings={settings}
          reduxTableName="registerDevices"
          hideSearch
          hideFilter
          AsideComponent={({ useTableProps }) => (
            <Button onClick={() => openModal('ADD_DEVICE', { onAddDevice: useTableProps.apiTable.fetchTableData })}>
              Add device
            </Button>
          )}
        />
      </div>
    </>
  );
};

export default RegisterDevices;
